import type {
 GridColDef,
 GridFilterItem,
} from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';

import type {
 ColPresetType,
 FilterFieldsType,
} from '../../../redux/clientConfiguration/clientConfigurationSlice';

/**
 * filterModel — the single place that knows how a filter *value* is shaped and
 * how it maps between the representations it lives in:
 *
 *   URL string ⇄ FilterFieldsType (preset / redux / localStorage) ⇄ GridFilterModel (the grid) → API
 *
 * Every shape decision lives here — "is this a model reference?", "is it a
 * multi-select?", "which grid operator does it need?", "is this column present
 * in the preset?". Keeping it in one module means a new value shape is a
 * one-file change instead of a hunt across the URL codec, the grid handler, the
 * sidebar and the table init hook (which is what made this fragile before).
 */

export type FieldValue = FilterFieldsType['fields'][string];

/** A value carrying an `Id` — i.e. a model/entity reference like `{ Id, Name }`. */
export const hasIdKey = (
 v: unknown,
): v is { Id: string | number; Name?: unknown } =>
 v !== null && typeof v === 'object' && 'Id' in (v as object);

/** True when the value is (or contains) model references and filters by id. */
export const isModelValue = (value: unknown): boolean =>
 hasIdKey(value) || (Array.isArray(value) && value.some(hasIdKey));

/**
 * True when a filter value carries nothing to filter by — `null`/`undefined`,
 * an empty string, an empty array, or an empty object. A `Date`, a number
 * (including `0`), and a populated model ref `{ Id, … }` are all non-empty.
 *
 * The single emptiness predicate for the grid→state→API direction: the sidebar
 * uses it to decide which fields become active filters, and `prepareFilters`
 * uses it to drop empty items before they hit the API — so an untouched
 * multi-select (e.g. an empty `ResultProducts` whose value is `[]`, which is
 * truthy) never leaks out as `ResultProducts.Id = []`.
 */
export const isEmptyFilterValue = (value: unknown): boolean => {
 if (value === null || value === undefined || value === '') return true;
 if (Array.isArray(value)) return value.length === 0;
 if (value instanceof Date) return false;
 if (typeof value === 'object') return Object.keys(value).length === 0;
 return false;
};

/**
 * Reduce model references to their ids, leaving plain scalars/arrays untouched:
 *   `{ Id, Name }`        → `[Id]`
 *   `[{ Id, Name }, …]`   → `[Id, …]`
 *   `['a','b']` / `'a'` / `5` → unchanged
 */
export const normalizeToIds = (value: unknown): unknown => {
 if (Array.isArray(value)) {
  return value.some(hasIdKey)
   ? value.map((i) => (hasIdKey(i) ? i.Id : i))
   : value;
 }
 if (hasIdKey(value)) return [value.Id];
 return value;
};

/**
 * The grid operator a value needs. Model/id values must use `isAnyOf` (MUI
 * matches against the id list); everything else keeps its stored operator and
 * falls back to `equals`. Sharing this between the URL→grid and grid→state
 * conversions is what stops them from disagreeing (the old code forced
 * `isAnyOf` in one path and used the stored operator in the other).
 */
export const deriveGridOperator = (
 isModel: boolean,
 storedOperator: string | null | undefined,
): string => (isModel ? 'isAnyOf' : storedOperator || 'equals');

/** Wrap a model/array/enum value as the array the sidebar form expects. */
export const toFormArrayValue = (value: unknown): unknown[] => {
 if (Array.isArray(value)) return value;
 return value ? [value] : [];
};

/**
 * Normalize a single `GridFilterItem` coming back from the grid: collapse model
 * objects to ids, format `Date` values, and pick the matching operator. Used by
 * `handleFilterModelChange` so the grid→state path obeys the same rules as the
 * URL→grid path in `filterFieldsToGridItems`.
 */
export const normalizeGridItem = (item: GridFilterItem): GridFilterItem => {
 const rawValue = item?.value;
 let value: unknown;
 let isModel = false;

 if (Array.isArray(rawValue)) {
  isModel = rawValue.some(hasIdKey);
  value = normalizeToIds(rawValue);
 } else if (rawValue instanceof Date) {
  value = dayjs(rawValue).format('YYYY-MM-DDTHH:mm:ss.SSS');
 } else {
  value = rawValue;
 }

 return {
  id: item.id || item.field,
  field: item.field,
  operator: deriveGridOperator(isModel, item.operator),
  value,
 };
};

/**
 * Merge active filters into a base preset, auto-adding lightweight column defs
 * for any filtered field the preset doesn't already show (so a URL-only filter
 * still has a visible/working column). Single helper shared by every
 * preset-merge site — previously this block was copy-pasted three times.
 *
 * The base preset's filters are *merged*, not replaced: it holds every filter
 * slot the user added — including the empty ones the URL omits — so the empty
 * slots stay visible in the sidebar after a reload. URL values win for keys
 * present in both.
 */
export const buildMergedPreset = (
 basePreset: ColPresetType,
 filterFields: FilterFieldsType,
): { merged: ColPresetType; hasMissingColumns: boolean } => {
 const existingFields = new Set(
  (basePreset.columns || []).map((c) => c.field),
 );
 const missingColumns: GridColDef[] = Object.keys(filterFields.fields)
  .filter((field) => !existingFields.has(field))
  .map((field) => ({ field }));

 const mergedFilters: FilterFieldsType = {
  fields: { ...basePreset.filters?.fields, ...filterFields.fields },
  operators: { ...basePreset.filters?.operators, ...filterFields.operators },
 };

 const merged: ColPresetType = {
  ...basePreset,
  filters: mergedFilters,
  ...(missingColumns.length > 0 && {
   columns: [...(basePreset.columns || []), ...missingColumns],
  }),
 };

 return { merged, hasMissingColumns: missingColumns.length > 0 };
};
